import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import type {
  FilterMyCompanyTasksDto,
  ChangeMyStatusDto,
  CreateMessageDto,
  UpdateMessageDto,
} from '@gokrew/models/tasks';
import {
  getMyEmployeeTasks,
  getMyEmployeeTasksSummary,
  getMyEmployeeTaskById,
  updateMyEmployeeTaskStatus,
  getMyEmployeeTaskActivity,
  getOrCreateEmployeeTaskThread,
  getEmployeeTaskThread,
  getEmployeeTaskThreadMessages,
  postEmployeeTaskMessage,
  editEmployeeTaskMessage,
  deleteEmployeeTaskMessage,
} from '@/api/employee-tasks';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';

const QK = {
  list: QueryKeys.EmpMyTasksList,
  detail: QueryKeys.EmpMyTasksDetail,
  thread: QueryKeys.EmpMyTasksThread,
  messages: QueryKeys.EmpMyTasksMessages,
  summary: QueryKeys.EmpMyTasksSummary,
  activity: QueryKeys.EmpMyTasksActivity,
} as const;

export function useGetMyEmployeeTasks(
  params: FilterMyCompanyTasksDto = {},
  enabled = true
) {
  return useQuery({
    queryKey: [QK.list, params],
    queryFn: () => getMyEmployeeTasks(params),
    enabled,
    placeholderData: keepPreviousData,
    select: (res) => res.data,
  });
}

export function useGetEmployeeTasksSummary() {
  return useQuery({
    queryKey: [QK.summary],
    queryFn: getMyEmployeeTasksSummary,
    select: (res) => res.data.data,
  });
}

export function useGetMyEmployeeTaskById(assignmentId: string | undefined) {
  return useQuery({
    queryKey: [QK.detail, assignmentId],
    queryFn: () => getMyEmployeeTaskById(assignmentId!),
    enabled: !!assignmentId,
    select: (res) => res.data.data,
  });
}

export function useUpdateMyEmployeeTaskStatus() {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.EmployeeTasks);

  return useMutation({
    mutationFn: ({
      assignmentId,
      payload,
    }: {
      assignmentId: string;
      payload: ChangeMyStatusDto;
    }) => updateMyEmployeeTaskStatus(assignmentId, payload),
    onSuccess: (_, { assignmentId }) => {
      toast.success(t('tasks.statusUpdated'));
      qc.invalidateQueries({ queryKey: [QK.list] });
      qc.invalidateQueries({ queryKey: [QK.detail, assignmentId] });
      qc.invalidateQueries({ queryKey: [QK.summary] });
      qc.invalidateQueries({ queryKey: [QK.activity, assignmentId] });
    },
    onError: () => {
      toast.error(t('tasks.failedUpdateStatus'));
    },
  });
}

export function useGetEmployeeTaskActivity(assignmentId: string | undefined) {
  return useQuery({
    queryKey: [QK.activity, assignmentId],
    queryFn: () => getMyEmployeeTaskActivity(assignmentId!),
    enabled: !!assignmentId,
    select: (res) => res.data.data,
  });
}

// ─── Thread ───────────────────────────────────────────────────

export function useGetOrCreateEmployeeTaskThread() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) =>
      getOrCreateEmployeeTaskThread(assignmentId),
    onSuccess: (_, assignmentId) => {
      qc.invalidateQueries({ queryKey: [QK.detail, assignmentId] });
    },
  });
}

export function useGetEmployeeTaskThread(threadId: string | undefined) {
  return useQuery({
    queryKey: [QK.thread, threadId],
    queryFn: () => getEmployeeTaskThread(threadId!),
    enabled: !!threadId,
    select: (res) => res.data,
  });
}

export function useGetEmployeeTaskThreadMessages(threadId: string | undefined) {
  return useQuery({
    queryKey: [QK.messages, threadId],
    queryFn: () => getEmployeeTaskThreadMessages(threadId!),
    enabled: !!threadId,
    select: (res) => res.data.data,
  });
}

export function usePostEmployeeTaskMessage() {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.EmployeeTasks);

  return useMutation({
    mutationFn: ({
      threadId,
      payload,
    }: {
      threadId: string;
      payload: CreateMessageDto;
    }) => postEmployeeTaskMessage(threadId, payload),
    onSuccess: (_, { threadId }) => {
      qc.invalidateQueries({ queryKey: [QK.messages, threadId] });
      qc.invalidateQueries({ queryKey: [QK.thread, threadId] });
      // Message events also show up in the assignment's activity timeline —
      // no assignmentId here (only threadId), so invalidate every cached
      // activity query rather than skip it.
      qc.invalidateQueries({ queryKey: [QK.activity] });
    },
    onError: () => {
      toast.error(t('tasks.failedSendMessage'));
    },
  });
}

export function useEditEmployeeTaskMessage() {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.EmployeeTasks);

  return useMutation({
    mutationFn: ({
      threadId,
      messageId,
      payload,
    }: {
      threadId: string;
      messageId: string;
      payload: UpdateMessageDto;
    }) => editEmployeeTaskMessage(threadId, messageId, payload),
    onSuccess: (_, { threadId }) => {
      qc.invalidateQueries({ queryKey: [QK.messages, threadId] });
      qc.invalidateQueries({ queryKey: [QK.activity] });
    },
    onError: () => {
      toast.error(t('tasks.failedEditMessage'));
    },
  });
}

export function useDeleteEmployeeTaskMessage() {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.EmployeeTasks);

  return useMutation({
    mutationFn: ({
      threadId,
      messageId,
    }: {
      threadId: string;
      messageId: string;
    }) => deleteEmployeeTaskMessage(threadId, messageId),
    onSuccess: (_, { threadId }) => {
      toast.success(t('tasks.messageDeleted'));
      qc.invalidateQueries({ queryKey: [QK.messages, threadId] });
      qc.invalidateQueries({ queryKey: [QK.activity] });
    },
    onError: () => {
      toast.error(t('tasks.failedDeleteMessage'));
    },
  });
}
